/**
 * Is a failed answer submission worth sending again?
 *
 * A leaf module with no imports, deliberately. It lived inside `useSubmitTest`,
 * where testing it meant importing the whole hook — and with it the component
 * library, which ships untranspiled ESM that Jest cannot parse. Pure logic that
 * cannot be reached without a rendering stack is pure logic that never gets a
 * test.
 *
 * Both directions cost something real. Retrying a deterministic 4xx spends up
 * to 1.8s per question to reach the same answer, and because the server deletes
 * the stored answer before validating the new one, each retry re-runs that
 * destructive delete. Not retrying a dropped connection loses the answer, which
 * is the failure the retry loop exists for.
 */
export const isRetriable = (error: unknown): boolean => {
	const status = (error as { status?: number | string })?.status

	// RTK Query reports transport failures with a string status —
	// FETCH_ERROR, TIMEOUT_ERROR, PARSING_ERROR — and HTTP failures with a number.
	if (typeof status === 'string') return true
	if (typeof status === 'number') return status >= 500

	// No recognisable status: an unexpected throw rather than a response. Retry
	// rather than silently dropping a student's answer on a shape we did not
	// anticipate.
	return true
}
